import * as React from "react";
import { Autocomplete, TextField } from "@mui/material";
import { usaCities, usaStates } from "typed-usa-states";

const stateNameToAbbr = usaStates.reduce((acc, s) => {
  acc[s.name] = s.abbreviation;
  return acc;
}, {});

/**
 * City autocomplete. Options show "City, ST". On select, calls onCityStateChange(city, stateCode).
 * @param {string} cityValue - Current city text
 * @param {string} stateValue - Current state code (abbreviation)
 * @param {(city: string, stateCode: string) => void} onCityStateChange - Called when user selects an option
 * @param {(city: string, stateCode: string) => void} [onSubmit] - Called when user confirms a selection with Enter or presses Enter with the dropdown closed
 * @param {object} [sx] - MUI sx prop
 * @param {string} [label='City']
 * @param {string} [placeholder]
 * @param {string} [size] - 'small' | 'medium'
 * @param {boolean} [required]
 */
export default function CityStateAutocomplete({
  cityValue,
  stateValue,
  onCityStateChange,
  onSubmit,
  sx,
  label = "City",
  placeholder = "e.g. Minneapolis",
  size = "medium",
  required = false,
}) {
  const enterPressedRef = React.useRef(false);
  const highlightedRef = React.useRef(null);
  // Tracks what the user actually typed, separate from the nav-preview display
  const typedValueRef = React.useRef("");

  const options = React.useMemo(() => {
    return usaCities.map((c) => {
      const stateCode = stateNameToAbbr[c.state] || c.state;
      return {
        city: c.name,
        stateName: c.state,
        stateCode,
        label: `${c.name}, ${stateCode}`,
      };
    });
  }, []);

  const selectedOption =
    cityValue && stateValue
      ? options.find(
          (o) =>
            o.city.toLowerCase() === cityValue.trim().toLowerCase() &&
            (o.stateCode === stateValue || o.stateName === stateValue)
        ) ?? null
      : null;

  const derivedInputValue =
    selectedOption?.label ??
    (cityValue && !stateValue ? `${cityValue}` : cityValue);

  // navLabel overrides what's shown in the input while arrowing through options
  const [navLabel, setNavLabel] = React.useState(null);
  const displayValue = navLabel ?? derivedInputValue;

  const filterOptions = (opts, { inputValue }) => {
    const trimmed = inputValue.trim();
    if (!trimmed) return [];
    const lower = trimmed.toLowerCase();
    const filtered = opts.filter(
      (o) =>
        o.city.toLowerCase().includes(lower) ||
        o.label.toLowerCase().includes(lower)
    );
    return filtered.slice(0, 50);
  };

  return (
    <Autocomplete
      options={options}
      filterOptions={filterOptions}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.label
      }
      value={selectedOption}
      inputValue={displayValue}
      openOnFocus={false}
      onHighlightChange={(_, option) => {
        highlightedRef.current = option;
        setNavLabel(option ? option.label : null);
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "clear") {
          setNavLabel(null);
          highlightedRef.current = null;
          typedValueRef.current = "";
          onCityStateChange("", "");
          return;
        }
        // User typed — exit nav mode and track what they typed
        if (reason === "input") {
          setNavLabel(null);
          highlightedRef.current = null;
          typedValueRef.current = newInputValue;

          const commaIdx = newInputValue.indexOf(", ");
          if (commaIdx > -1) {
            const city = newInputValue.slice(0, commaIdx).trim();
            const statePart = newInputValue.slice(commaIdx + 2).trim().toUpperCase();
            const match = options.find(
              (o) =>
                o.city.toLowerCase() === city.toLowerCase() &&
                (o.stateCode === statePart || o.stateName === statePart)
            );
            if (match) {
              onCityStateChange(match.city, match.stateCode);
              return;
            }
          }
          onCityStateChange(newInputValue, "");
        }
      }}
      onChange={(_, newValue) => {
        if (newValue && typeof newValue === "object") {
          // User selected an option from the dropdown
          enterPressedRef.current = false;
          setNavLabel(null);
          highlightedRef.current = null;
          onCityStateChange(newValue.city, newValue.stateCode);
          onSubmit?.(newValue.city, newValue.stateCode);
        } else if (!newValue) {
          // User cleared the input
          onCityStateChange("", "");
        }
        // freeSolo string (Enter on unselected text): ignore here — the onKeyDown
        // setTimeout already has a submit queued with the current cityValue/stateValue.
      }}
      isOptionEqualToValue={(option, value) =>
        option && value && option.city === value.city && option.stateCode === value.stateCode
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size={size}
          required={required}
          inputProps={{
            ...params.inputProps,
            onKeyDown: (e) => {
              const highlighted = highlightedRef.current;

              if ((e.key === "Tab" || e.key === "Enter") && highlighted) {
                onCityStateChange(highlighted.city, highlighted.stateCode);
                setNavLabel(null);
                highlightedRef.current = null;
                if (e.key === "Tab") {
                  // Tab won't trigger MUI's onChange, so call onSubmit here
                  onSubmit?.(highlighted.city, highlighted.stateCode);
                }
                // Enter: MUI's onChange will fire and call onSubmit from there
              }

              params.inputProps?.onKeyDown?.(e);

              if (e.key === "Enter" && !highlighted && onSubmit) {
                // No option was highlighted — submit with whatever city/state is current
                enterPressedRef.current = true;
                setTimeout(() => {
                  if (enterPressedRef.current) {
                    enterPressedRef.current = false;
                    onSubmit(cityValue, stateValue);
                  }
                }, 0);
              }
            },
          }}
          onBlur={() => {
            if (cityValue) {
              const titled = cityValue
                .split(" ")
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                .join(" ");
              if (titled !== cityValue) onCityStateChange(titled, stateValue);
            }
          }}
        />
      )}
      sx={{ minWidth: 200, ...sx }}
      freeSolo={true}
      componentsProps={{
        paper: {
          sx: {
            bgcolor: "background.paper",
            color: "text.primary",
          },
        },
      }}
      ListboxProps={{
        sx: {
          maxHeight: 280,
          "& .MuiAutocomplete-option": {
            color: "text.primary",
          },
          "& .MuiAutocomplete-option.Mui-focused": {
            bgcolor: "action.hover",
            color: "text.primary",
          },
        },
      }}
    />
  );
}
